import LZString from 'lz-string';

/**
 * Encodes selection labels into a compact URL parameter so the treatment
 * detail page can render the search recap chips.
 *
 * Encoding pipeline:
 *   selectionLabels -> {shortKey: [labels]} -> JSON -> LZ-String compressed,
 *   URL-safe base64.
 *
 * If the encoded payload still exceeds STORAGE_THRESHOLD it is stashed in
 * sessionStorage and a tiny `s:<id>` reference is returned instead. This keeps
 * the URL under gunicorn's `--limit-request-line` cap while preserving the
 * recap for in-tab navigation. Shared links above the threshold load fine but
 * the receiver won't see the chips (the chips are a UX nicety; the page works
 * regardless).
 */

// Long field names compress well, but we still trim the JSON before LZ to avoid
// hitting the storage fallback for moderate selections.
const FIELD_KEY_MAP = {
  'treatment_cultures.culture': 'c',
  'treatment_cultures.cultural_practices': 'cp',
  'treatment_type': 'tt',
  'treatment_enemies.enemy': 'e',
  'intervention_period': 'ip',
  'application_type': 'at',
  'cultures': 'c',
};
const INVERSE_FIELD_KEY_MAP = Object.fromEntries(
  Object.entries(FIELD_KEY_MAP).map(([long, short]) => [short, long])
);

const STORAGE_THRESHOLD = 2500;
const STORAGE_PREFIX = 's:';
const SESSION_STORAGE_KEY = 'sage:treatment-sc:';

const compactFromLabels = (selectionLabels) => {
  const compact = {};
  Object.entries(selectionLabels).forEach(([field, labels]) => {
    if (!Array.isArray(labels) || labels.length === 0) return;
    const shortKey = FIELD_KEY_MAP[field] || field;
    compact[shortKey] = labels.map((l) => l.label || l.name || l.value || l);
  });
  return compact;
};

const expandCompact = (compact) => {
  const selectionLabels = {};
  Object.entries(compact).forEach(([shortKey, labels]) => {
    const field = INVERSE_FIELD_KEY_MAP[shortKey] || shortKey;
    selectionLabels[field] = labels.map((label) => ({ label }));
  });
  return selectionLabels;
};

const stashInSessionStorage = (payload) => {
  try {
    const id = Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem(SESSION_STORAGE_KEY + id, payload);
    return STORAGE_PREFIX + id;
  } catch {
    return '';
  }
};

const readFromSessionStorage = (id) => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY + id);
  } catch {
    return null;
  }
};

// Legacy format: btoa(encodeURIComponent(JSON.stringify(compact))) with full
// field names. Kept so links generated by the previous code keep rendering the
// recap until users refresh the search page.
const tryDecodeLegacy = (encoded) => {
  try {
    const parsed = JSON.parse(decodeURIComponent(atob(encoded)));
    return expandCompact(parsed);
  } catch {
    return null;
  }
};

export function encodeSearchCriteria(selectionLabels) {
  if (!selectionLabels) return '';
  try {
    const compact = compactFromLabels(selectionLabels);
    if (Object.keys(compact).length === 0) return '';
    const payload = LZString.compressToEncodedURIComponent(JSON.stringify(compact));
    if (payload.length <= STORAGE_THRESHOLD) return payload;
    return stashInSessionStorage(payload);
  } catch {
    return '';
  }
}

export function decodeSearchCriteria(encoded) {
  if (!encoded) return null;
  try {
    let payload = encoded;
    if (encoded.startsWith(STORAGE_PREFIX)) {
      payload = readFromSessionStorage(encoded.slice(STORAGE_PREFIX.length));
      if (!payload) return null;
    }
    const decompressed = LZString.decompressFromEncodedURIComponent(payload);
    if (decompressed) return expandCompact(JSON.parse(decompressed));
    return tryDecodeLegacy(encoded);
  } catch {
    return tryDecodeLegacy(encoded);
  }
}
